// https://developer.mozilla.org/en-US/docs/Web/CSS/length
type Length = {
  value: number;
  unit: string;
};

const parseLength = (length: string, defaultUnit?: string): Length => {
  // grab the numbers from length string
  try {
    const value = length.match(/[\d.]+/)?.[0] || '0';
    const unit = length.match(/[a-z%]+$/i)?.[0] || defaultUnit || '';
    return {
      value: Number.parseFloat(value) || 16,
      unit,
    };
  } catch {
    return {
      value: 16,
      unit: 'px',
    };
  }
};

// rules:

// Body:
// Under 14px → force to 16px on mobile
// 14-16px → increase 20% on mobile (16.8-19.2px)
// 16-18px → increase 15% on mobile (18.4-20.7px)
// 18px+ → keep same size
// Max: 20px

// Body text (rem/em):
// Under 0.9rem → force to 1rem on mobile
// 0.9-1rem → increase 20% on mobile (1.08-1.2rem)
// 1-1.1rem → increase 15% on mobile (1.15-1.265rem)
// 1.1rem+ → keep same size
// Max: 1.25rem

// Headings:
// Over 48px → reduce 30% on mobile (becomes 33.6px+)
// 32-48px → reduce 25% on mobile (becomes 24-36px)
// Under 32px → reduce 20% on mobile (becomes ≤25.6px)
// Max: 70px desktop, 48px mobile

// Headings (rem/em):
// Over 3rem → reduce 30% on mobile (becomes 2.1rem+)
// 2-3rem → reduce 25% on mobile (becomes 1.5-2.25rem)
// Under 2rem → reduce 20% on mobile (becomes ≤1.6rem)
// Max: 4.375rem desktop, 3rem mobile

const computeFallbackMobileFontSize = (fontSize: number, unit: string) => {
  let mobileFontSize = fontSize;

  if (unit === 'px') {
    const isHeading = fontSize > 25;

    if (isHeading) {
      // apply heading rules
      if (fontSize > 48) {
        mobileFontSize = fontSize * 0.7;
      } else if (fontSize > 32) {
        mobileFontSize = fontSize * 0.75;
      } else {
        mobileFontSize = fontSize * 0.8;
      }
      if (mobileFontSize > 70) {
        mobileFontSize = 70;
      }
    } else {
      // apply body rules
      if (fontSize <= 14) {
        mobileFontSize = 16;
      } else if (fontSize <= 16) {
        mobileFontSize = fontSize * 1.2;
      } else if (fontSize <= 18) {
        mobileFontSize = fontSize * 1.15;
      }

      if (mobileFontSize > 20) {
        mobileFontSize = 20;
      }
    }
  } else {
    const isHeading = fontSize > 1.25;

    // apply heading rules
    if (isHeading) {
      if (fontSize > 3) {
        mobileFontSize = fontSize * 0.7;
      } else if (fontSize > 2) {
        mobileFontSize = fontSize * 0.75;
      } else {
        mobileFontSize = fontSize * 0.8;
      }

      if (mobileFontSize > 4.375) {
        mobileFontSize = 4.375;
      }
    } else {
      if (fontSize < 0.75) {
        mobileFontSize = 1;
      } else if (fontSize < 0.875) {
        mobileFontSize = fontSize * 1.2;
      } else if (fontSize < 1) {
        mobileFontSize = fontSize * 1.15;
      }

      if (mobileFontSize > 1.25) {
        mobileFontSize = 1.25;
      }
    }
  }

  return mobileFontSize;
};

export const getFontSizeVariables = (fontSize?: number | string, mobileFontSize?: number | string, prefix?: string) => {
  if (!fontSize) {
    if (mobileFontSize) {
      return {
        [prefix ? `--${prefix}-font-size` : '--font-size']: '100%',
        [prefix ? `--${prefix}-mobile-font-size` : '--mobile-font-size']: mobileFontSize,
      };
    }

    return {
      [prefix ? `--${prefix}-font-size` : '--font-size']: '100%',
      [prefix ? `--${prefix}-mobile-font-size` : '--mobile-font-size']: '16px',
    };
  }

  let value: number;
  let unit: string;

  if (typeof fontSize === 'number') {
    value = fontSize;
    unit = 'px';
  } else {
    const length = parseLength(fontSize);
    value = length.value;
    unit = length.unit;
  }

  return {
    [prefix ? `--${prefix}-font-size` : '--font-size']: `${value}${unit}`,
    [prefix ? `--${prefix}-mobile-font-size` : '--mobile-font-size']:
      mobileFontSize || `${computeFallbackMobileFontSize(value, unit)}${unit}`,
  };
};
